import { sortBy } from 'lodash';

import { EMPTY_STRING } from '@e-socna/empty';
import { GroupingModeEnum } from '@e-socna/data-layer';
import { invariant } from '@e-socna/utils';
import type { Id } from '@e-socna/data-layer';

import { renumber } from '../../../utils/renumber.util';

interface IndexedItem {
  readonly _id: Id;
  readonly index: number;
}

export interface FormServiceUsagesGroup<Usage extends IndexedItem> {
  readonly __typename?: 'ResolvableServiceUsagesGroup';
  readonly _id: Id;
  readonly groupingMode: GroupingModeEnum;
  readonly index: number;
  readonly isOptional?: boolean;
  readonly label?: string | null;
  readonly serviceUsages: readonly Usage[];
}

type Groups<Usage extends IndexedItem> =
  readonly FormServiceUsagesGroup<Usage>[];

const toManualLabel = (label: string) => {
  const trimmedLabel = label.trim();

  invariant(
    trimmedLabel !== EMPTY_STRING,
    'A manual service usages group label cannot be empty',
  );

  return trimmedLabel;
};

const findGroup = <Usage extends IndexedItem>(
  groups: Groups<Usage>,
  groupId: Id,
) => {
  const group = groups.find(({ _id }) => _id === groupId);

  invariant(!!group, 'Service usages group not found');

  return group;
};

const countManualGroups = <Usage extends IndexedItem>(groups: Groups<Usage>) =>
  groups.filter(({ groupingMode }) => groupingMode === GroupingModeEnum.Manual)
    .length;

export const switchToManualGrouping = <Usage extends IndexedItem>({
  groups,
  label,
  newGroupId,
}: Readonly<{
  groups: Groups<Usage>;
  label: string;
  newGroupId: Id;
}>): Groups<Usage> => [
  {
    __typename: 'ResolvableServiceUsagesGroup',
    _id: newGroupId,
    groupingMode: GroupingModeEnum.Manual,
    index: 0,
    isOptional: false,
    label: toManualLabel(label),
    serviceUsages: groups[0]?.serviceUsages ?? [],
  },
];

export const mergeGroupsIntoSingleton = <Usage extends IndexedItem>({
  groups,
  newGroupId,
}: Readonly<{
  groups: Groups<Usage>;
  newGroupId: Id;
}>): Groups<Usage> => [
  {
    __typename: 'ResolvableServiceUsagesGroup',
    _id: newGroupId,
    groupingMode: GroupingModeEnum.Singleton,
    index: 0,
    isOptional: false,
    label: null,
    serviceUsages: sortBy(groups, 'index')
      .flatMap(({ serviceUsages }) => sortBy(serviceUsages, 'index'))
      .map((serviceUsage, index) => ({ ...serviceUsage, index })),
  },
];

export const createGroup = <Usage extends IndexedItem>({
  groups,
  isOptional,
  label,
  newGroupId,
}: Readonly<{
  groups: Groups<Usage>;
  isOptional: boolean;
  label: string;
  newGroupId: Id;
}>): Groups<Usage> => {
  invariant(
    countManualGroups(groups) === groups.length,
    'A service usages group can only be created in manual grouping mode',
  );

  return [
    ...groups,
    {
      __typename: 'ResolvableServiceUsagesGroup',
      _id: newGroupId,
      groupingMode: GroupingModeEnum.Manual,
      index: groups.length,
      isOptional,
      label: toManualLabel(label),
      serviceUsages: [],
    },
  ];
};

export const updateGroup = <Usage extends IndexedItem>({
  groupId,
  groups,
  isOptional,
  label,
}: Readonly<{
  groupId: Id;
  groups: Groups<Usage>;
  isOptional: boolean;
  label: string;
}>): Groups<Usage> => {
  invariant(
    findGroup(groups, groupId).groupingMode === GroupingModeEnum.Manual,
    'Only a manual service usages group can be updated',
  );

  const manualLabel = toManualLabel(label);

  return groups.map((group) =>
    group._id === groupId
      ? { ...group, isOptional, label: manualLabel }
      : group,
  );
};

export const removeGroup = <Usage extends IndexedItem>({
  groupId,
  groups,
}: Readonly<{
  groupId: Id;
  groups: Groups<Usage>;
}>): Groups<Usage> => {
  invariant(
    findGroup(groups, groupId).serviceUsages.length === 0,
    'A service usages group that still has service usages cannot be removed',
  );
  invariant(
    countManualGroups(groups) > 1,
    'The last manual service usages group cannot be removed',
  );

  return renumber(
    sortBy(
      groups.filter(({ _id }) => _id !== groupId),
      'index',
    ),
  );
};

export const reorderGroups = <Usage extends IndexedItem>({
  groups,
  orderedGroupIds,
}: Readonly<{
  groups: Groups<Usage>;
  orderedGroupIds: readonly Id[];
}>): Groups<Usage> => {
  invariant(
    orderedGroupIds.length === groups.length &&
      groups.every(({ _id }) => orderedGroupIds.includes(_id)),
    'The ordered group ids must match the service usages groups',
  );

  return groups.map((group) => ({
    ...group,
    index: orderedGroupIds.indexOf(group._id),
  }));
};

export const moveServiceUsageToGroup = <Usage extends IndexedItem>({
  groups,
  serviceUsageId,
  targetGroupId,
}: Readonly<{
  groups: Groups<Usage>;
  serviceUsageId: Id;
  targetGroupId: Id;
}>): Groups<Usage> => {
  const sourceGroup = groups.find(({ serviceUsages }) =>
    serviceUsages.some(({ _id }) => _id === serviceUsageId),
  );
  const movedServiceUsage = sourceGroup?.serviceUsages.find(
    ({ _id }) => _id === serviceUsageId,
  );
  const targetGroup = findGroup(groups, targetGroupId);

  invariant(
    !!sourceGroup && !!movedServiceUsage,
    'Service usage not found in any service usages group',
  );
  invariant(
    sourceGroup._id !== targetGroupId,
    'A service usage cannot be moved to its current group',
  );

  return groups.map((group) => {
    if (group._id === sourceGroup._id) {
      return {
        ...group,
        serviceUsages: renumber(
          sortBy(
            group.serviceUsages.filter(({ _id }) => _id !== serviceUsageId),
            'index',
          ),
        ),
      };
    }

    if (group._id === targetGroupId) {
      return {
        ...group,
        serviceUsages: [
          ...group.serviceUsages,
          { ...movedServiceUsage, index: targetGroup.serviceUsages.length },
        ],
      };
    }

    return group;
  });
};
